import React from 'react';
import ReactDOM from 'react-dom';
import {
	buildEScooterItem,
	buildRegiorad,
	buildStadtmobilFlinksterItem,
	buildStellaItem,
	SharingButton,
	sharingMappings,
} from 'shared';
import * as PropTypes from 'prop-types';

const buildStationAddress = addressData =>
	addressData.map((data, i) => {
		if (i === addressData.length) {
			return (
				<span key={`address-${data}-br`}>
					{data}
					{i === addressData.length - 1 && <br />}
				</span>
			);
		}
		return <span key={`address-${data}`}>{data}</span>;
	});

/** Presentational component for sharer tooltips */
function SharingTooltip(props) {
	return (
		<div
			className={`marker__tooltip marker__tooltip--sharing marker__tooltip--${props.station.type
				.toLowerCase()
				.replace(' ', '-')}`}
		>
			<h2 className="tooltipcontent__title">
				{sharingMappings[props.station.type] || props.station.type}
			</h2>
			<p>{props.adjustedItems.title}</p>
			<p>
				<strong>Verfügbare Fahrzeuge:</strong> {props.adjustedItems.availableVehicleCount}
			</p>
			<p>{props.adjustedItems.subtitle}</p>
			<p>
				{props.adjustedItems.vehicletypesLength > 1 ? (
					<span>
						Fahrzeugtypen: <br />
					</span>
				) : (
					'Fahrzeugtyp: '
				)}
				{props.adjustedItems.vehicletypes}
			</p>
			<p className="tooltipcontent__address">
				{buildStationAddress(props.adjustedItems.address)}
			</p>
			{/* To adjust the color of the button, visit
			packages/shared/src/components/SharingButton/index.js and change the class for the type */}
			<SharingButton
				iosDeeplink={props.adjustedItems.iosDeeplink}
				iosStore={props.adjustedItems.iosStore}
				androidDeeplink={props.adjustedItems.androidDeeplink}
				androidStore={props.adjustedItems.androidStore}
				windowsDeeplink={props.adjustedItems.windowsDeeplink}
				windowsStore={props.adjustedItems.windowsStore}
				uri={props.adjustedItems.uri || props.customUri}
				openPopup={props.openPopup}
			>
				{props.adjustedItems.linkname}
			</SharingButton>
			{props.additionalContent}
		</div>
	);
}

SharingTooltip.propTypes = {
	station: PropTypes.any,
	adjustedItems: PropTypes.shape({
		id: PropTypes.any,
		title: PropTypes.any,
		availableVehicleCount: PropTypes.number,
		subtitle: PropTypes.string,
		address: PropTypes.array,
		vehicletypesLength: PropTypes.any,
		vehicletypes: PropTypes.any,
		iosDeeplink: PropTypes.any,
		iosStore: PropTypes.any,
		androidDeeplink: PropTypes.any,
		androidStore: PropTypes.any,
		windowsDeeplink: PropTypes.any,
		windowsStore: PropTypes.any,
		uri: PropTypes.string,
		linkname: PropTypes.any,
		type: PropTypes.string,
		icon: PropTypes.string,
	}),
	customUri: PropTypes.string,
	openPopup: PropTypes.func,
	additionalContent: PropTypes.any,
};

/**
 * Renders a popover for sharers, with basic info about the station or
 * vehicle and a button to recieve more info.
 *
 * @param station
 * @param operators
 * @param tooltip
 * @param additionalContent
 * @param openPopup
 */
/** Maps lowercased station.type to its builder function and fallback URI; add new providers here. */
const PROVIDERS = {
	stadtmobil: {
		build: (s, o) => buildStadtmobilFlinksterItem(s, o, 'Fahrzeuge prüfen'),
		defaultUri: 'https://stuttgart.stadtmobil.de/privatkunden/',
	},
	flinkster: {
		build: (s, o) => buildStadtmobilFlinksterItem(s, o, 'Fahrzeuge prüfen'),
		defaultUri: 'https://www.flinkster.de/index.php',
	},
	'ökostadt renningen': {
		build: (s, o) => buildStadtmobilFlinksterItem(s, o, 'Fahrzeuge prüfen'),
		defaultUri: 'https://carsharing-renningen.de/',
	},
	stella: {
		build: (s, o) => buildStellaItem(s, o, 'Fahrzeuge prüfen'),
		defaultUri: 'https://www.stella-sharing.de/',
	},
	lime: {
		build: (s, o) => buildEScooterItem(s, o.Lime?.[0], 'Fahrzeug prüfen'),
		defaultUri: 'https://www.li.me/de-de/',
	},
	voi: {
		build: (s, o) => buildEScooterItem(s, o.VOI?.[0], 'Fahrzeug prüfen'),
		defaultUri: 'https://www.voi.com/de/',
	},
	dott: {
		build: (s, o) => buildEScooterItem(s, o.Dott?.[0], 'Fahrzeug prüfen'),
		defaultUri: 'https://ridedott.com/de/',
	},
	bolt: {
		build: (s, o) => buildEScooterItem(s, o.Bolt?.[0], 'Fahrzeug prüfen'),
		defaultUri: 'https://www.bolt.de/',
	},
	zeus: {
		build: (s, o) => buildEScooterItem(s, o.Zeus?.[0], 'Fahrzeug prüfen'),
		defaultUri: 'https://zeusscooters.com/',
	},
	regiorad: {
		build: (s, o) => buildRegiorad(s, o.Regiorad?.[0], 'Fahrzeug prüfen'),
		defaultUri: 'https://www.regioradstuttgart.de/de/start',
	},
};

export default function renderSharingTooltip(
	station,
	operators,
	tooltip,
	additionalContent,
	openPopup = () => {},
) {
	const providerKey = station.type.toString().toLowerCase();
	const provider = PROVIDERS[providerKey];

	if (!provider) return;

	const adjustedItems = provider.build(station, operators);
	const customUri = adjustedItems.uri || provider.defaultUri;

	ReactDOM.render(
		<SharingTooltip
			station={station}
			adjustedItems={adjustedItems}
			customUri={customUri}
			openPopup={openPopup}
			additionalContent={additionalContent}
		/>,
		tooltip,
	);
}
